import { useState, useCallback, useEffect, createContext, useContext, useRef } from 'react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: string
  message: string
  type: ToastType
  duration?: number
}

interface ToastContextType {
  toast: (message: string, type?: ToastType, duration?: number) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

const icons: Record<ToastType, string> = {
  success: '✓',
  error:   '✕',
  warning: '⚠',
  info:    'ℹ',
}

const colors: Record<ToastType, string> = {
  success: '#39FF14',
  error:   '#FF4800',
  warning: '#FFB800',
  info:    '#00F0FF',
}

function ToastItem({ item, onRemove }: { item: ToastItem; onRemove: (id: string) => void }) {
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setVisible(true))
    // Auto dismiss
    timerRef.current = setTimeout(() => {
      setVisible(false)
      setTimeout(() => onRemove(item.id), 300)
    }, item.duration ?? 3500)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [item.id, item.duration, onRemove])

  const color = colors[item.type]

  return (
    <div
      onClick={() => { setVisible(false); setTimeout(() => onRemove(item.id), 300) }}
      style={{
        transform: visible ? 'translateX(0)' : 'translateX(110%)',
        transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        borderLeft: `3px solid ${color}`,
        background: 'rgba(13,13,21,0.98)',
        borderColor: 'rgba(58,58,90,0.5)',
        boxShadow: `0 0 16px ${color}20`,
      }}
      className="flex items-start gap-3 rounded-lg px-4 py-3 cursor-pointer min-w-64 max-w-xs"
    >
      <span className="font-bold text-sm mt-0.5 flex-shrink-0" style={{ color, fontFamily: 'Rajdhani, system-ui' }}>
        {icons[item.type]}
      </span>
      <p className="text-sm leading-snug text-[#EAF6FF]" style={{ fontFamily: 'Inter, sans-serif' }}>{item.message}</p>
    </div>
  )
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const remove = useCallback((id: string) => {
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  const toast = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
    const id = crypto.randomUUID()
    setToasts(t => [...t, { id, message, type, duration }])
  }, [])

  const success = useCallback((m: string) => toast(m, 'success'), [toast])
  const error   = useCallback((m: string) => toast(m, 'error'), [toast])
  const info    = useCallback((m: string) => toast(m, 'info'), [toast])

  return (
    <ToastContext.Provider value={{ toast, success, error, info }}>
      {children}
      {/* Toast stack — top-right desktop, top-center mobile */}
      <div className="fixed z-[100] top-20 right-4 md:right-6 flex flex-col gap-2 items-end pointer-events-none">
        {toasts.map(item => (
          <div key={item.id} className="pointer-events-auto">
            <ToastItem item={item} onRemove={remove} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
